import { AUTH0_LOGIN_COMPLETED_SESSION_KEY, AUTH0_LOGIN_METHOD_SESSION_KEY } from '@libs/core';

import { BREAKPOINTS } from '../constants';

export const AUTH0_LOGIN_METHODS = ['login page', 'login modal'] as const;

export type Auth0LoginMethod = (typeof AUTH0_LOGIN_METHODS)[number];

let hasConsumedCompletedLogin = false;

function isAuth0LoginMethod(value: string | null | undefined): value is Auth0LoginMethod {
    return value === 'login page' || value === 'login modal';
}

function withSessionStorage<T>({ fallback, read }: { fallback: T; read: (storage: Storage) => T }): T {
    try {
        if (typeof sessionStorage === 'undefined') {
            return fallback;
        }

        return read(sessionStorage);
    } catch {
        return fallback;
    }
}

function mutateSessionStorage(mutate: (storage: Storage) => void): void {
    withSessionStorage({
        fallback: undefined,
        read: storage => {
            mutate(storage);
            return undefined;
        },
    });
}

/**
 * Desktop and tablet map to the legacy full-page sign-in surface; mobile web
 * maps to the legacy sign-in drawer. Auth0 always uses Universal Login, so
 * viewport is the durable stand-in for `login_method`.
 */
export function resolveAuth0LoginMethod(): Auth0LoginMethod {
    try {
        if (globalThis.window.matchMedia(`(max-width: ${BREAKPOINTS.md - 1}px)`).matches) {
            return 'login modal';
        }
    } catch {
        return 'login page';
    }

    return 'login page';
}

function readPersistedLoginMethod(): Auth0LoginMethod | undefined {
    return withSessionStorage({
        fallback: undefined,
        read: storage => {
            const stored = storage.getItem(AUTH0_LOGIN_METHOD_SESSION_KEY);
            return isAuth0LoginMethod(stored) ? stored : undefined;
        },
    });
}

/**
 * Captures `login_method` before the full-page Auth0 redirect so it is still
 * available after Universal Login returns to PetSmart. Call only from
 * login/signup entry points — not from already-authenticated Auth0 flows.
 */
export function persistAuth0LoginMethod(): void {
    mutateSessionStorage(storage => {
        storage.setItem(AUTH0_LOGIN_METHOD_SESSION_KEY, resolveAuth0LoginMethod());
    });
}

/**
 * One-time completed-login signal. Arms analytics only when
 * `persistAuth0LoginMethod` ran before this callback. Change-email and other
 * already-authenticated Auth0 returns share `/auth/callback` and must not fire
 * `login`. Storage failures must not throw — callback redirect still has to run.
 */
export function markAuth0LoginCompleted(): void {
    hasConsumedCompletedLogin = false;

    const loginMethod = readPersistedLoginMethod();
    if (!loginMethod) {
        return;
    }

    mutateSessionStorage(storage => {
        storage.setItem(AUTH0_LOGIN_COMPLETED_SESSION_KEY, loginMethod);
    });
}

/**
 * True when a completed Auth0 login is waiting to be reported. Does not consume
 * the signal, so a Strict Mode remount can still fire after the first effect
 * is cancelled mid-claims-fetch.
 */
export function hasPendingAuth0LoginCompleted(): boolean {
    if (hasConsumedCompletedLogin) {
        return false;
    }

    return withSessionStorage({
        fallback: false,
        read: storage => isAuth0LoginMethod(storage.getItem(AUTH0_LOGIN_COMPLETED_SESSION_KEY)),
    });
}

/**
 * Returns the preserved `login_method` for a newly completed Auth0 login and
 * removes the signal so callback re-renders, destination reloads, and later
 * visits with an existing Auth0 session cannot fire again.
 */
export function consumeAuth0LoginCompleted(): Auth0LoginMethod | null {
    if (hasConsumedCompletedLogin) {
        return null;
    }

    const stored = withSessionStorage({
        fallback: null,
        read: storage => storage.getItem(AUTH0_LOGIN_COMPLETED_SESSION_KEY),
    });

    mutateSessionStorage(storage => {
        storage.removeItem(AUTH0_LOGIN_COMPLETED_SESSION_KEY);
        storage.removeItem(AUTH0_LOGIN_METHOD_SESSION_KEY);
    });

    if (!isAuth0LoginMethod(stored)) {
        return null;
    }

    hasConsumedCompletedLogin = true;
    return stored;
}
